import { TextArea } from "react-aria-components";
import { useEffect, useState } from "react";
import { IPicklistItem } from "../../../List/DisposalsPickList";
import { StatusDto } from "../../../../../enums/Domain/Status";
import { signal } from "@preact/signals-react";
import SidePanelInfo from "./SidePanelInfo";
import { EntityNames } from "../../../../../enums/EntityNames";
import SidePanelList from "./SidePanelList";

export interface IPackingBoxDisposalRange {
    id: string,
    displayName: string,
    accountName: string,
    type: string,
    yearStart?: number,
    yearEnd?: number,
    year?: string, // Backend list may return year as string (e.g. "2023" or "2023 > 2024")
}

export default function SidePanelDisposalPickingItemInputs({ data, handleChange }: { data: IPicklistItem, handleChange: (input: string) => (id: string, e: string | number | boolean | StatusDto) => void; }) {
    // React state to manage the main data
    const [notes, setNotes] = useState<string>("");

    // Signals to manage state for keybinding responsiveness
    const selectedItemSignal = signal(data.id);

    function handleNotesChanged(e: React.ChangeEvent<HTMLTextAreaElement>) {
        setNotes(e.target.value);
        handleChange("notes")(data.id, e.target.value);
    }

    function displayYears(range: IPackingBoxDisposalRange) {
        if (range.yearStart !== undefined && range.yearEnd !== undefined) {
            if (range.yearStart === range.yearEnd) {
                return range.yearStart.toString();
            }
            return `${range.yearStart} > ${range.yearEnd}`;
        }
        if (range.year) {
            return range.year;
        }
        return "";
    }

    useEffect(() => {
        if (!data) return;

        if (notes !== data.notes) {
            setNotes(data.notes);
        }

        selectedItemSignal.value = data.id;
    }, [data?.id, data?.notes]);

    return (
        <div className="margin__bottom--xs">
            <SidePanelInfo
                values={[
                    { label: "Reference", value: data.reference },
                    { label: "Sequence No", value: data.sequenceNumber },
                    { label: "Volume", value: data.volume },
                    { label: "Years", value: data.years }
                ]}
            />
            <SidePanelList<IPackingBoxDisposalRange>
                id={data.packingBoxId}
                entityName={EntityNames.ranges}
                searchLabel={undefined}
                onAddNewClicked={undefined}
                editFunction={undefined}
                itemData={undefined}
                loadingRowCount={3}
                searchFilter={undefined}
                fixedHeight={235}
                renderRowContent={(item, edit, remove) => (
                    <>
                        <td>
                            <div className="side-panel-table__cell-content">
                                <p className="small small--2 textWrap">{item.displayName}</p>
                                <p className="small small--2 textWrap">Years: {displayYears(item)}</p>
                            </div>
                        </td>
                        <td>
                            <div className="side-panel-table__cell-content">
                                <p className="small small--2 textWrap">{item.type}</p>
                            </div>
                        </td>
                        <td>
                            <div className="side-panel-table__cell-content">
                                <p className="small small--2 textWrap">{item.accountName}</p>
                            </div>
                        </td>
                    </>
                )}
                displayColumns={
                    <>
                        <th><p className="small"></p></th>
                        <th><p className="small"></p></th>
                        <th><p className="small"></p></th>
                    </>
                }
            />
            <div className="side-panel__info-box margin__top--s">
                <p className="small">Current Location</p>
                <div className="side-panel__breadcrumb-wrapper">
                    <div className="side-panel__blue-background">
                        <p className="breadcrumb">{data.breadcrumb}</p>
                    </div>
                </div>
            </div>
            <div className="margin__top--s">
                <p className="small">CellNass Notes</p>
                <TextArea value={notes}
                    onChange={handleNotesChanged}
                    aria-label="Notes"
                    className={"pick-list__side-panel-text-area pick-list__side-panel-text-area--small"} />
            </div>
        </div>
    );
}
